import { Component, OnInit, Renderer2 } from '@angular/core';
import { App } from '@capacitor/app';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';

// Services
import { ApiService, APIRoutes, PageRoutes } from './services/api.service';
import { StorageService } from './services/storage.service';
import { Keys } from './common/keys';
import { User } from './model/user';
import { ScriptService } from './services/script.service';
import { environment } from '../environments/environment';
import { InAppPurchaseService } from './services/in-app-purchase.service';

// OneSignal Capacitor Plugin
import OneSignal, {
  PushSubscriptionChangedState,
} from 'onesignal-cordova-plugin';
import { LogService } from './services/log.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  public appPages = [
    {
      title: 'Create Transaction',
      url: PageRoutes.add_transaction.addSlash(),
      icon: 'add-circle-outline',
    },
    {
      title: 'Logout',
      url: '#logout',
      icon: 'log-out-outline',
    },
  ];

  user: User = {};

  constructor(
    private renderer: Renderer2,
    private scriptService: ScriptService,
    private platform: Platform,
    private api: ApiService,
    private router: Router,
    private storage: StorageService,
    private inAppPurchaseService: InAppPurchaseService,
    private logService: LogService
  ) {
    this.api.onUser.subscribe((res) => {
      this.user = res;
    });
  }

  async ngOnInit() {
    this.api.broker_mode = (await this.storage.get(Keys.broker_mode)) || false;
    if (!this.api.isDesktop()) {
      // ✅ Native mobile app (iOS/Android)
      this.initOneSignalMobile();

      // Inizializza RevenueCat
      this.inAppPurchaseService.initialize();
    } else {
      // 🌍 Web/Desktop
      this.initOneSignalWeb();
    }

    // 🔗 Handle deeplinks
    App.addListener('appUrlOpen', (event: any) => {
      console.log('DEEPLINK:', event.url);
      const slug = event.url.split('//')[1];
      if (slug === 'settings') {
        this.router.navigateByUrl('/dashboard/settings');
      }
    });

    const username = await this.storage.get(Keys.username);
    const token = await this.storage.get(Keys.api_token_key);
    const bioactive = await this.storage.get(Keys.is_biometric_active);

    // Log in backend
    this.logService.log('App Init', {
      username: username,
      token: token,
      bioactive: bioactive,
      isDesktop: this.api.isDesktop(),
    });

    if (username != null && token != null) {
      if (bioactive && !this.api.isDesktop())
        this.router.navigate([PageRoutes.login.addSlash()], {
          replaceUrl: true,
        });
    }
  }

  // --- NAVIGATION ---
  agents() {
    this.router.navigate([PageRoutes.agents.addSlash()]);
  }
  createTransaction() {
    this.api.post(APIRoutes.create_transaction, {}, (res) => {
      this.router.navigate([
        PageRoutes.add_transaction.addSlash() + '/' + res.uid,
      ]);
    });
  }
  async logout() {
    await this.api.logout();
    this.router.navigate([PageRoutes.login.addSlash()], { replaceUrl: true });
  }

  // --- MOBILE (CAPACITOR) ONESIGNAL INIT ---
  private async initOneSignalMobile(): Promise<void> {
    const appId = '4e9b4d59-8d41-4e0b-9ea2-cd2397a0b038';

    console.log('Initializing OneSignal (Mobile)...');

    OneSignal.Debug.setLogLevel(6);
    OneSignal.initialize(appId);

    OneSignal.Notifications.requestPermission(false).then(
      (accepted: boolean) => {
        console.log('User accepted notifications: ' + accepted);
      }
    );

    // Foreground notification handler
    OneSignal.Notifications.addEventListener(
      'foregroundWillDisplay',
      (event) => {
        console.log('OneSignal: Notification in foreground:', event);
        event.getNotification().display(); // Ensure it shows
      }
    );

    // Notification opened
    OneSignal.Notifications.addEventListener('click', async (event) => {
      console.log('OneSignal: Notification opened:', event);
      const data = event.notification.additionalData;
      if (data) {
        await this.storage.set(Keys.notification_received, data);
        // Add navigation logic if needed
      }
    });

    const playerId = await OneSignal.User.getOnesignalId();

    if (playerId) {
      console.log('OneSignal Mobile Player ID:', playerId);
      await this.storage.set(Keys.mobile_push, playerId);

      this.api.post(
        APIRoutes.save_player_id,
        { type: 'mobile', player_id: playerId, subscription_id: null },
        (res) => console.log('Saved mobile player ID:', res)
      );
    }
  }

  // --- WEB ONESIGNAL INIT ---
  private initOneSignalWeb(): void {
    console.log('Initializing OneSignal (Web)...');
    const scriptElement = this.scriptService.loadJsScript(
      this.renderer,
      'https://cdn.onesignal.com/sdks/OneSignalSDK.js'
    );

    scriptElement.onload = () => {
      const w = window as any;
      w.OneSignal = w.OneSignal || [];

      w.OneSignal.push(() => {
        w.OneSignal.init({
          appId: '4e9b4d59-8d41-4e0b-9ea2-cd2397a0b038',
          safari_web_id:
            'web.onesignal.auto.32866db8-e87d-48d6-8bf4-a7b54cca5ee4',
          allowLocalhostAsSecureOrigin: true,
        });

        // Listen for permission changes
        w.OneSignal.on('notificationPermissionChange', (permissionChange) => {
          const currentPermission = permissionChange.to;
          if (currentPermission === 'granted') {
            this.saveWebPlayerId();
          }
        });

        // Always try to fetch user ID
        this.saveWebPlayerId();
      });
    };

    scriptElement.onerror = () => {
      console.error('Error loading OneSignal SDK for Web.');
    };
  }

  private async saveWebPlayerId() {
    const w = window as any;
    const userId = await w.OneSignal.getUserId();
    if (userId) {
      console.log('OneSignal Web Player ID:', userId);
      await this.storage.set(Keys.web_push, userId);
      this.api.post(
        APIRoutes.save_player_id,
        { type: 'web', player_id: userId },
        (res) => console.log('Saved web player ID:', res)
      );
    }
  }
}
